<script lang="ts">
  // About dialog — surfaces the frontend bundle's identification (compiled in
  // at build time via VITE_* env vars) AND the running backend's identification
  // (fetched live from GET /api/version). Front and back ship on independent
  // cadences; showing both honestly is the whole point of this dialog.
  //
  // Modal: uses the native <dialog> element controlled via showModal()/close()
  // (no third-party modal library in the project today). The `open` prop is
  // the single source of truth — an $effect syncs it to the DOM element's
  // open/closed state both ways.

  import { APP_VERSION, BUILD_SHA, BUILD_TIME, BUILD_CHANNEL, fetchApiVersion, type ApiVersion } from '$lib/api/version';
  import { IconButton, Badge } from '$lib/ui';
  import { X } from '$lib/ui/icons';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';

  type Props = {
    open: boolean;
    onClose: () => void;
  };

  const { open, onClose }: Props = $props();

  let dialogEl: HTMLDialogElement | undefined = $state(undefined);
  let apiVersion = $state<ApiVersion | null>(null);
  let apiLoading: boolean = $state(false);
  let apiError: string | null = $state(null);

  $effect(() => {
    if (!dialogEl) return;
    if (open && !dialogEl.open) {
      dialogEl.showModal();
      // Fire the fetch each time the dialog opens. Cheap (one tiny GET) and
      // avoids stale data when the user opens it after a backend redeploy.
      loadApiVersion();
    } else if (!open && dialogEl.open) {
      dialogEl.close();
    }
  });

  async function loadApiVersion(): Promise<void> {
    apiLoading = true;
    apiError = null;
    apiVersion = null;
    try {
      apiVersion = await fetchApiVersion();
    } catch (e) {
      apiError = (e as Error).message;
    } finally {
      apiLoading = false;
    }
  }

  function handleClose(): void {
    onClose();
  }

  function handleNativeClose(): void {
    // Native close (e.g. user pressed Escape) — surface that back to parent
    // state so `open` doesn't get out of sync with the DOM.
    if (open) onClose();
  }

  const showFeChip = $derived(BUILD_CHANNEL !== 'production');
  const showApiChip = $derived(apiVersion !== null && apiVersion.channel !== 'production');
</script>

<dialog
  bind:this={dialogEl}
  onclose={handleNativeClose}
  class="about-dialog"
  aria-labelledby="about-title"
>
  <div class="panel">
    <header>
      <h2 id="about-title">{t(UiKeys.AboutDialog.Title)}</h2>
      <IconButton label={t(UiKeys.AboutDialog.CloseAria)} icon={X} size="sm" onclick={handleClose} />
    </header>

    <dl class="rows">
      <div class="row">
        <dt>{t(UiKeys.AboutDialog.App)}</dt>
        <dd>
          <span class="ver">v{APP_VERSION}</span>
          {#if showFeChip}
            <Badge tone="info">{t(UiKeys.AboutDialog.DevChannel)}</Badge>
          {/if}
        </dd>
      </div>

      <div class="row">
        <dt>{t(UiKeys.AboutDialog.Api)}</dt>
        <dd>
          {#if apiLoading}
            <span class="muted">{t(UiKeys.AboutDialog.Loading)}</span>
          {:else if apiError}
            <span class="err">{t(UiKeys.AboutDialog.Unavailable, { Error: apiError })}</span>
          {:else if apiVersion}
            <span class="ver">v{apiVersion.version}</span>
            {#if showApiChip}
              <Badge tone="info">{t(UiKeys.AboutDialog.DevChannel)}</Badge>
            {/if}
          {/if}
        </dd>
      </div>

      <div class="row">
        <dt>{t(UiKeys.AboutDialog.BuiltFe)}</dt>
        <dd>
          <span class="time">{BUILD_TIME}</span>
          <span class="sha">{BUILD_SHA}</span>
        </dd>
      </div>

      <div class="row">
        <dt>{t(UiKeys.AboutDialog.BuiltApi)}</dt>
        <dd>
          {#if apiLoading}
            <span class="muted">{t(UiKeys.AboutDialog.Loading)}</span>
          {:else if apiError}
            <span class="muted">—</span>
          {:else if apiVersion}
            <span class="time">{apiVersion.builtAt}</span>
            <span class="sha">{apiVersion.sha}</span>
          {/if}
        </dd>
      </div>
    </dl>
  </div>
</dialog>

<style lang="scss">
  .about-dialog {
    border: none;
    border-radius: 10px;
    padding: 0;
    background: transparent;
    max-width: 28rem;
    width: 90vw;
  }

  .about-dialog::backdrop {
    background: rgb(0 0 0 / 0.4);
  }

  .panel {
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 1.25rem 1.5rem 1.5rem;
    font-family: var(--font-ui);
    color: var(--ink);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  h2 {
    margin: 0;
    font-size: var(--text-guide);
  }

  .rows {
    margin: 0;
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.5rem 1rem;
  }

  .row {
    display: contents;
  }

  dt {
    color: var(--ink-mute);
    font-size: var(--text-body);
    align-self: baseline;
  }

  dd {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    font-size: var(--text-body);
  }

  .ver {
    font-family: var(--font-mono);
    font-weight: 400;
  }

  .time {
    font-family: var(--font-mono);
    font-size: var(--text-body);
    color: var(--ink-soft);
  }

  .sha {
    font-family: var(--font-mono);
    font-size: var(--text-meta);
    color: var(--ink-mute);
  }

  .err {
    color: var(--color-recording-text);
    font-size: var(--text-body);
  }
</style>
